import { useState } from "react";
import { FiChevronDown, FiPaperclip, FiCalendar } from "react-icons/fi";
import { GOAL_COLORS } from "../../utils/constants";
import { formatDate } from "../../utils/format";
import StatusBadge from "../common/StatusBadge";
import ProgressBar from "../common/ProgressBar";
import EmptyState from "../common/EmptyState";

export function weightedPct(weight, achieved) {
  if (achieved === null || achieved === undefined || weight === null || weight === undefined) return "—";
  const value = Math.round(((Number(weight) * Math.min(100, Math.max(0, Number(achieved)))) / 100) * 100) / 100;
  return `${value}% of ${weight}%`;
}

export default function GoalList({ goals = [], renderActions, renderExtra, showAchievement = true, emptyTitle = "No goals yet", emptyText, emptyAction }) {
  const [openId, setOpenId] = useState(null);

  if (!goals.length) {
    return <EmptyState title={emptyTitle} text={emptyText} action={emptyAction} />;
  }

  return (
    <ul className="goal-list">
      {goals.map((goal, i) => {
        const open = openId === goal.id;
        const ach = goal.managerAchievement ?? goal.achievement;
        return (
          <li key={goal.id} className={`goal-row ${open ? "is-open" : ""}`} style={{ "--goal-color": GOAL_COLORS[i % GOAL_COLORS.length] }}>
            <div className="goal-row-main">
              <button type="button" className="goal-toggle" onClick={() => setOpenId(open ? null : goal.id)} aria-expanded={open}>
                <FiChevronDown size={16} aria-hidden="true" />
                <span className="sr-only">{open ? "Hide details" : "Show details"}</span>
              </button>
              <div className="goal-title-block">
                <p className="goal-title">{goal.title}</p>
                <p className="goal-meta">
                  <span>{goal.categoryName}</span>
                  <StatusBadge status={goal.priority} dot={false}>{goal.priority} priority</StatusBadge>
                  {goal.source && <span className="tag tag-info">{goal.source}</span>}
                  <span className="goal-due"><FiCalendar size={12} aria-hidden="true" /> {formatDate(goal.dueDate)}</span>
                </p>
              </div>
              <div className="goal-weight">
                <span className="goal-weight-value">{goal.weightage}%</span>
                <span className="goal-weight-label">goal %</span>
              </div>
              <div className="goal-target">
                <span className="goal-target-value">
                  {goal.baseline !== "" && goal.baseline !== null && goal.baseline !== undefined ? `${goal.baseline} → ` : ""}{goal.target}
                </span>
                <span className="goal-target-label">{goal.unit}</span>
              </div>
              {showAchievement && (
                <div className="goal-progress">
                  <ProgressBar value={ach} tone={ach >= 100 ? "success" : "primary"} showValue size="sm" label="Achieved %" />
                </div>
              )}
              <div className="goal-status"><StatusBadge status={goal.status} /></div>
              {renderActions && <div className="goal-actions">{renderActions(goal)}</div>}
            </div>
            {open && (
              <div className="goal-detail">
                <div className="goal-detail-grid">
                  <div>
                    <p className="detail-label">Expected outcome</p>
                    <p className="detail-text">{goal.description || "—"}</p>
                  </div>
                  <div>
                    <p className="detail-label">Employee update</p>
                    <p className="detail-text">{goal.employeeComments || "No update yet."}</p>
                    {goal.evidence && (
                      <p className="detail-file"><FiPaperclip size={13} aria-hidden="true" /> {goal.evidence}</p>
                    )}
                  </div>
                  <div>
                    <p className="detail-label">Manager comments</p>
                    <p className="detail-text">{goal.managerComments || "No comments yet."}</p>
                  </div>
                  <div className="detail-numbers">
                    <p><span>Self-rated achievement</span><strong>{goal.achievement ?? "—"}{goal.achievement != null ? "%" : ""}</strong></p>
                    <p><span>Manager-agreed achievement</span><strong>{goal.managerAchievement ?? "—"}{goal.managerAchievement != null ? "%" : ""}</strong></p>
                    <p><span>Goal percentage</span><strong>{goal.weightage}%</strong></p>
                    <p><span>Earned towards goal score</span><strong>{weightedPct(goal.weightage, ach)}</strong></p>
                  </div>
                </div>
                {renderExtra && renderExtra(goal)}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
